import SubHeader from "components/common/SubHeader";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { getClassProduct } from "api/class";
import { getTravelProduct } from "api/travel";
import {
    getProduct,
    getProductShippingFee,
    updateProductShippingFee,
} from "api/store";
import { createOrder, createProductOrder } from "api/payment";
import { searchUsers, createUserAddressList } from "api/user";
import defaultImage from "assets/images/imgs/class_default_img.jpg";
import useUserStore from "@store/useUserStore";
import CommonModal from "components/common/CommonModal";
import Postcode from "components/common/Postcode";
import { useSnackbar } from "contexts/SnackbarContext";
import { trackBeginCheckout, trackViewItem } from "utils/analytics";

const PaymentInfo = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();
    const path = location.pathname;
    const params = new URLSearchParams(location.search);
    const purchaseCnt = Number(params.get("purchaseCnt")) || 1;
    const skuCode = params.get("sku");
    const skuId = params.get("id");
    const departureId = params.get("departureId");
    const startDate = params.get("startDate");
    const endDate = params.get("endDate");
    const lessonOptionSelections = location.state?.lessonOptionSelections || {};
    const lessonOptionDelta = location.state?.optionPriceDelta || 0;
    const optionSummary = location.state?.optionSummary || [];
    // 상품 상세에서 넘어온 "사용자 입력 옵션" 답변(질문 순서와 동일). 없으면 빈 배열.
    const customerInputs = location.state?.customerInputs || [];
    const userInfo = useUserStore((state) => state.userInfo) || {};
    const setUserInfo = useUserStore((state) => state.setUserInfo);
    const productAddress =
        Object.keys(userInfo)?.length > 0
            ? userInfo?.addresses?.find((address) => address.is_primary) ??
              userInfo?.addresses?.[0] ??
              null
            : null;

    const { showError } = useSnackbar();

    const [productType, setProductType] = useState(null); // lesson or travel or product
    const [product, setProduct] = useState(null);
    const [shippingFee, setShippingFee] = useState(null);
    const [shippingMemo, setShippingMemo] = useState("");
    const [address, setAddress] = useState(productAddress);
    const [isOpen, setIsOpen] = useState(false);
    // 배송지 변경 모달 내에서 신규 배송지 입력 폼 노출 여부
    const [showAddAddress, setShowAddAddress] = useState(false);
    const [optionPrice, setOptionPrice] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // 어드민에서 설정한 사용자 입력 옵션 질문 목록. 답변(customerInputs)과 인덱스로 매칭한다.
    const customerInputOptions =
        productType === "product" &&
        Array.isArray(product?.customer_input_options)
            ? product.customer_input_options
            : [];

    // 투어 동행인: 예약 인원이 2명 이상이면 (인원수 - 1)명의 동행인 정보를 수기 입력받는다.
    // 동행인은 travel 상품에서만 사용한다.
    const companionCount =
        productType === "travel" ? Math.max(0, purchaseCnt - 1) : 0;
    const [companions, setCompanions] = useState([]);
    // 동행인 이름 검색 드롭다운 상태(한 번에 한 행만 활성). null이면 닫힘.
    const [companionSearch, setCompanionSearch] = useState({
        index: null,
        results: [],
    });
    const companionDebounceRef = useRef(null);
    const companionReqIdRef = useRef(0);

    // purchaseCnt/상품 타입이 확정되면 동행인 입력 슬롯 수를 맞춘다.
    useEffect(() => {
        setCompanions((prev) => {
            if (prev.length === companionCount) return prev;
            const next = prev.slice(0, companionCount);
            while (next.length < companionCount) {
                next.push({ name: "", hp: "", gender: "", user_id: null });
            }
            return next;
        });
    }, [companionCount]);

    useEffect(() => {
        return () => {
            if (companionDebounceRef.current)
                clearTimeout(companionDebounceRef.current);
        };
    }, []);

    // 동행인 이름 입력: 회원 검색을 디바운스로 호출한다. 수기 수정 시 회원 연결은 해제.
    const handleCompanionNameChange = (index, value) => {
        setCompanions((prev) =>
            prev.map((c, i) =>
                i === index
                    ? { ...c, name: value, user_id: null, from_member: false }
                    : c
            )
        );
        if (companionDebounceRef.current)
            clearTimeout(companionDebounceRef.current);
        if (value.trim().length < 2) {
            setCompanionSearch({ index: null, results: [] });
            return;
        }
        companionDebounceRef.current = setTimeout(async () => {
            const reqId = ++companionReqIdRef.current;
            const res = await searchUsers(value.trim());
            if (reqId === companionReqIdRef.current) {
                setCompanionSearch({ index, results: res.data || [] });
            }
        }, 300);
    };

    const handleCompanionFieldChange = (index, field, value) => {
        setCompanions((prev) =>
            prev.map((c, i) => (i === index ? { ...c, [field]: value } : c))
        );
    };

    // 검색 결과 회원 선택 시 이름/연락처/성별 자동 기입(성별·아이디는 없을 수 있음).
    const selectCompanionUser = (index, user) => {
        setCompanions((prev) =>
            prev.map((c, i) =>
                i === index
                    ? {
                          name: user.name || c.name,
                          hp: user.hp || "",
                          gender: user.gender || "",
                          user_id: user.id ?? null,
                          from_member: true,
                      }
                    : c
            )
        );
        setCompanionSearch({ index: null, results: [] });
    };

    const isCompanionsValid =
        companionCount === 0 ||
        companions.every(
            (c) => c.name.trim() && c.hp.trim() && c.gender
        );

    // 주소별 배송비(POST)가 적용됐는지 추적. 적용 후 도착한 GET 기본값이
    // 이를 덮어쓰지 못하게 하는 race 가드.
    const addressFeeAppliedRef = useRef(false);
    // 배송비 재계산 요청 시퀀스. 응답이 out-of-order로 도착해도 최신 요청만 반영한다.
    const shippingReqSeqRef = useRef(0);

    useEffect(() => {
        // 일단 product 일때만 옵션이 존재
        if (productType === "product" && product) {
            const skuList = product?.variants;
            const findAdditionalPrice = skuList.find(
                (sku) => sku.id === Number(skuId)
            );
            setOptionPrice(findAdditionalPrice?.final_price || null);
        }
    }, [skuId, product, productType]);

    // userInfo가 마운트 후 뒤늦게 채워지는 경우(persist 재수화 지연·세션 갱신 등)
    // 선택 배송지를 동기화한다. 사용자가 이미 고른 주소가 있으면 덮어쓰지 않는다.
    useEffect(() => {
        if (address) return;
        const list = userInfo?.addresses;
        if (!list?.length) return;
        setAddress(list.find((addr) => addr.is_primary) ?? list[0]);
    }, [userInfo?.addresses, address]);

    useEffect(() => {
        if (path.includes("lesson")) {
            setProductType("lesson");
            getClassProduct(id).then((res) => {
                // 숨김/삭제 상품 등 단건 조회 실패 시 null → 결제 진행 불가 안내
                if (!res?.id) {
                    showError("상품 정보를 불러오지 못했습니다.");
                    return;
                }
                setProduct(res);
                // GA4: 상품 조회 이벤트 추적
                trackViewItem({
                    item_id: res.id,
                    item_name: res.name,
                    item_category: "lesson",
                    price: res.discount_price || res.price,
                });
            });
        } else if (path.includes("tour")) {
            setProductType("travel");
            getTravelProduct(id).then((res) => {
                setProduct(res);
                // GA4: 상품 조회 이벤트 추적
                trackViewItem({
                    item_id: res.id,
                    item_name: res.name,
                    item_category: "tour",
                    price: res.discount_price || res.price,
                });
            });
        } else if (path.includes("product")) {
            setProductType("product");
            getProduct(id).then((res) => {
                setProduct(res);
                // GA4: 상품 조회 이벤트 추적
                trackViewItem({
                    item_id: res.id,
                    item_name: res.name,
                    item_category: "product",
                    price: res.discount_price || res.price,
                });
            });
            getProductShippingFee(id).then((res) => {
                // 주소별 배송비(POST)가 이미 적용됐으면 기본값(GET)으로 덮어쓰지 않는다.
                if (addressFeeAppliedRef.current) return;
                if (!res?.shipping) return;
                setShippingFee(res.shipping.total_shipping_fee ?? 0);
            });
        }
    }, [id, path]);

    const clickPaymentBtn = async () => {
        if (isSubmitting) return;
        if (!product) return;

        if (productType === "product" && !address) {
            alert("배송지를 입력해주세요.");
            return;
        }

        // ALWAYS 투어: 일정 정보 검증 (상세 페이지에서 전달받음)
        if (productType === "travel" && product?.tour_type === "ALWAYS") {
            if (!departureId && !startDate) {
                showError("투어 일정을 선택해주세요.");
                return;
            }
        }

        // 투어 동행인 정보 검증: 모두 채워지지 않으면 주문 생성하지 않는다.
        if (productType === "travel" && companionCount > 0 && !isCompanionsValid) {
            showError("동행인 정보(이름·연락처·성별)를 모두 입력해주세요.");
            return;
        }

        // GA4: 결제 시작 이벤트 추적
        const totalAmount =
            productType === "product" ? 
            (optionPrice || product.discount_price) * purchaseCnt 
            : productType === "travel" ?
                product.price
                 : product.discount_price * purchaseCnt;

        trackBeginCheckout({
            value: totalAmount,
            items: [
                {
                    item_id: product.id,
                    item_name: product.name,
                    item_category: productType,
                    price:
                        optionPrice || product.discount_price || product.price,
                    quantity: purchaseCnt,
                },
            ],
        });

        setIsSubmitting(true);

        try {
            if (productType === "product") {
                const productParams = {
                    product_id: product.id,
                    quantity: purchaseCnt,
                    variant_id: skuId ? Number(skuId) : null,
                    shipping_info: {
                        recipient_name: userInfo.name,
                        recipient_phone: userInfo.hp,
                        address: address?.road_address,
                        address_detail: address?.detail_address,
                        postal_code: address?.zipcode,
                        shipping_memo: shippingMemo,
                    },
                    // 주문 시점 질문과 결합해 스냅샷되도록 답변을 질문 순서대로 전송한다.
                    ...(customerInputOptions.length > 0 && {
                        customer_inputs: customerInputs,
                    }),
                };
                const order = await createProductOrder(productParams);
                if (order.message === "주문이 생성되었습니다.") {
                    const paymentInfo = order.data.payment_params;
                    if (!window.innopay) {
                        alert("결제 모듈이 아직 준비되지 않았습니다.");
                        return;
                    }
                    window.innopay.goPay({
                        ...paymentInfo,
                        buyerEmail: userInfo.user_email,
                    });
                } else {
                    showError(order.message);
                }
            } else {
                const params = {
                    product_type: productType.toUpperCase(),
                    product_id: product.id,
                    total_amount: productType === "lesson"
                        ? (Number(product.discount_price ?? product.price) + Number(lessonOptionDelta)) * purchaseCnt
                        : (product.discount_price ?? product.price) * purchaseCnt,
                    logo_url: "https://buccl.com/images/logo.png",
                    quantity: purchaseCnt,
                };

                if (productType === "lesson" && Object.keys(lessonOptionSelections).length > 0) {
                    params.lesson_option_selections = lessonOptionSelections;
                }

                if (productType === "travel") {
                    if (product.tour_type === "ALWAYS") {
                        if (departureId) {
                            params.departure_id = Number(departureId);
                        } else if (startDate) {
                            params.desired_start_date = startDate;
                            params.desired_end_date = endDate || startDate;
                        }
                    }
                    if (companions.length > 0) {
                        params.companions = companions.map(c => ({
                            name: c.name.trim(),
                            hp: c.hp.trim(),
                            gender: c.gender,
                            ...(c.user_id && { user_id: c.user_id }),
                        }));
                    }
                }

                const order = await createOrder(params);
                if (order.message === "주문이 생성되었습니다.") {
                    const paymentInfo = order.data.payment_params;
                    if (!window.innopay) {
                        alert("결제 모듈이 아직 준비되지 않았습니다.");
                        return;
                    }
                    window.innopay.goPay(paymentInfo);
                } else {
                    showError(order.message || "주문 생성에 실패했습니다.");
                }
            }
        } catch (err) {
            showError("결제에 실패했습니다. 다시 시도해주세요.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const getFinalPrice = () => {
        if (productType === "product") {
            if (product.discount_price === null) {
                if (optionPrice !== null) {
                    return Number(optionPrice * purchaseCnt);
                } else {
                    return Number(product.price * purchaseCnt);
                }
            } else {
                if (optionPrice !== null) {
                    return Number(optionPrice * purchaseCnt);
                } else {
                    return Number(product.discount_price * purchaseCnt);
                }
            }
        } else if (productType === "lesson") {
            return Number(
                (Number(product.discount_price || product.price) + Number(lessonOptionDelta)) * purchaseCnt
            );
        } else if(productType === 'travel'){
            return Number(product.price * purchaseCnt);
        } else{
          return Number(
            (product.discount_price ??  product.price) * purchaseCnt
        );
        }
    };

    // 선택 배송지(자동/수동 무관)로 주소별 배송비를 재계산한다.
    // 백엔드 POST /products/{id}/shipping-fee/ 는 address + postal_code 가 필수이고,
    // variant_id 는 ProductVariant 의 숫자 PK(=skuId)를 기대한다(주문 생성 API와 동일).
    const refreshShippingFeeByAddress = useCallback(
        (addr) => {
            if (!product?.id || !addr?.road_address || !addr?.zipcode) return;
            const addrText = `${addr.road_address} ${
                addr.detail_address ?? ""
            }`.trim();
            const reqSeq = ++shippingReqSeqRef.current;
            updateProductShippingFee(product.id, {
                address: addrText,
                postal_code: addr.zipcode,
                quantity: purchaseCnt,
                variant_id: skuId ? Number(skuId) : undefined,
            }).then((res) => {
                // 더 최신 재계산 요청이 발사됐으면 이 응답은 stale → 무시
                if (reqSeq !== shippingReqSeqRef.current) return;
                if (res === null) {
                    showError(
                        "배송비를 불러오지 못했습니다. 잠시 후 다시 시도해주세요."
                    );
                    return;
                }
                if (res?.shipping) {
                    // 주소별 배송비 적용 → 이후 GET 응답이 덮어쓰지 못하도록 표시
                    addressFeeAppliedRef.current = true;
                    setShippingFee(res.shipping.total_shipping_fee ?? 0);
                }
            });
        },
        [product?.id, skuId, purchaseCnt, showError]
    );

    // address가 자동선택(대표/폴백)이든 수동선택(모달)이든 확정되면 배송비를 재계산해
    // 화면 표시액을 백엔드 청구 기준과 일치시킨다. product 로드 지연 race는 가드로 처리.
    useEffect(() => {
        if (productType !== "product") return;
        refreshShippingFeeByAddress(address);
    }, [address, productType, refreshShippingFeeByAddress]);

    // 배송지 변경 모달에서 신규 배송지를 저장한다. Postcode 컴포넌트의 saveAddress 콜백.
    // 저장 성공 시 전역 userInfo.addresses에 반영하고 새 배송지를 선택해
    // address 변경 useEffect가 배송비를 재계산하도록 한다.
    const handleSaveNewAddress = (id, postcode, isPrimary) => {
        // 이 화면에서는 신규 추가만 처리한다(id === null). 삭제/수정은 마이페이지에서.
        if (id !== null) return;
        createUserAddressList(postcode)
            .then((res) => {
                if (!res) {
                    showError("배송지 저장에 실패했습니다. 다시 시도해주세요.");
                    return;
                }
                // 대표 주소로 지정 시 기존 주소들의 is_primary를 해제한다.
                const prevAddresses = (userInfo?.addresses || []).map((addr) =>
                    isPrimary ? { ...addr, is_primary: false } : addr
                );
                setUserInfo({
                    ...userInfo,
                    addresses: [...prevAddresses, res],
                });
                // 새 배송지 선택 → address useEffect가 배송비 재계산
                setAddress(res);
                setShowAddAddress(false);
                setIsOpen(false);
            })
            .catch(() => {
                showError("배송지 저장에 실패했습니다. 다시 시도해주세요.");
            });
    };

    const freeThreshold = product?.shipping_policy?.free_threshold ?? null;
    const productSubtotal =
        productType === "product" && product ? getFinalPrice() : 0;
    const isFreeShipping =
        productType === "product" &&
        freeThreshold !== null &&
        productSubtotal >= Number(freeThreshold);
    const effectiveShippingFee = isFreeShipping ? 0 : (shippingFee ?? 0);

    return (
        <div className="page-payment-info">
            <SubHeader title="결제" />
            {product && (
                <div className="payment-info-wrap">
                    <h2 className="title">주문 상품</h2>
                    <div className="info-card">
                        <ul>
                            {product?.images?.[0]?.image ? (
                                <li className="thumbnail">
                                    <img
                                        src={product.images[0].image}
                                        alt={product.name}
                                        onError={(e) => { e.currentTarget.src = defaultImage; }}
                                    />
                                    <p>{`[${product.brand || ""}] ${
                                        product.name
                                    }`}</p>
                                </li>
                            ) : (
                                <li>
                                    <p>상품명</p>
                                    <p>{`[${product.brand ?? product.location}] ${product.name}`}</p>
                                </li>
                            )}
                            <li>
                                <p>주문수량</p>
                                <p>{purchaseCnt}개</p>
                            </li>
                            {productType !== "travel" && (
                                <li>
                                    <p>옵션</p>
                                    {productType === "lesson" && optionSummary.length > 0 ? (
                                        <p className="product-option">
                                            {optionSummary.map(s => `${s.groupName}: ${s.selectedNames.join(", ")}`).join(" / ")}
                                        </p>
                                    ) : (
                                        <p>{skuCode || "-"}</p>
                                    )}
                                </li>
                            )}
                            {customerInputOptions.map((question, i) => (
                                <li key={`customer-input-${i}`}>
                                    <p>{question}</p>
                                    <p className="product-option">
                                        {customerInputs[i] || "-"}
                                    </p>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <h2 className="title">결제 정보</h2>
                    <ul className="price-section">
                        <li>
                            <p className="price-label">상품가격</p>
                            <p className="price-value">
                                {Number(
                                    product.price * purchaseCnt
                                ).toLocaleString()}
                                원
                            </p>
                        </li>
                        {productType === "product" && optionPrice !== null && (
                            <li>
                                <p className="price-label">옵션 추가금</p>
                                <p className="price-value">
                                    +
                                    {Math.abs(
                                        Number(
                                            optionPrice - product.discount_price
                                        ) * purchaseCnt
                                    ).toLocaleString()}
                                    원
                                </p>
                            </li>
                        )}
                        {lessonOptionDelta > 0 && (
                            <li>
                                <p className="price-label">옵션 추가금</p>
                                <p className="price-value">
                                    +{(lessonOptionDelta * purchaseCnt).toLocaleString()}원
                                </p>
                            </li>
                        )}
                        {product.discount_price && (
                            <li>
                                <p className="price-label">할인금액</p>
                                <p className="price-value">
                                    -
                                    {(
                                        Number(product.price * purchaseCnt) -
                                        Number(
                                            product.discount_price * purchaseCnt
                                        )
                                    ).toLocaleString()}
                                    원
                                </p>
                            </li>
                        )}

                        <li>
                            <p className="price-label ">배송비</p>
                            <p className="price-value bold">
                                {productType === "product"
                                    ? isFreeShipping || effectiveShippingFee === 0
                                        ? "무료"
                                        : `${effectiveShippingFee.toLocaleString()}원`
                                    : "없음"}
                            </p>
                        </li>
                        <li className="price-total">
                            <p className="price-label ">총 결제금액</p>
                            <p className="price-value bold">
                                {(
                                    getFinalPrice() + effectiveShippingFee
                                ).toLocaleString()}
                                원
                            </p>
                        </li>
                    </ul>

                    <h2 className="title">{productType === "travel" ? "예약 정보" : "배송 정보"}</h2>
                    <ul className="price-section">
                        <li>
                            <p className="price-label">주문자 명</p>
                            <p className="price-value">{userInfo?.name}</p>
                        </li>
                        <li>
                            <p className="price-label">연락처</p>
                            <p className="price-value">{userInfo?.hp}</p>
                        </li>
                        {productType === "travel" && product.tour_type === "ALWAYS" && (departureId || startDate) && (
                            <li>
                                <p className="price-label">투어 일정</p>
                                <p className="price-value">
                                    {departureId
                                        ? `기존 회차 참여 (회차 #${departureId})`
                                        : `${startDate}${endDate && endDate !== startDate ? ` ~ ${endDate}` : ""}`
                                    }
                                </p>
                            </li>
                        )}
                        {productType === "travel" && companionCount > 0 && (
                            <li className="companion-block">
                                <p className="price-label companion-title">
                                    동행인 정보 (이름, 성별, 연락처 필수)
                                    
                                </p>
                                <div className="companion-forms">
                                    {companions.map((c, idx) => (
                                        <div key={idx} className="companion-card">
                                            <div className="companion-card__head">
                                                동행인 {idx + 1}
                                                {c.from_member && (
                                                    <span className="companion-badge">회원</span>
                                                )}
                                            </div>
                                            <div className="companion-field companion-field--name">
                                                <input
                                                    type="text"
                                                    className="companion-input"
                                                    value={c.name}
                                                    onChange={(e) =>
                                                        handleCompanionNameChange(idx, e.target.value)
                                                    }
                                                    placeholder="회원명 검색 혹은 직접 입력"
                                                    autoComplete="off"
                                                />
                                                {companionSearch.index === idx &&
                                                    companionSearch.results.length > 0 &&
                                                    c.name.trim().length >= 2 && (
                                                        <div className="companion-dropdown">
                                                            {companionSearch.results.map((user, uIdx) => (
                                                                <button
                                                                    type="button"
                                                                    key={user.id ?? uIdx}
                                                                    className="companion-dd-item"
                                                                    onClick={() =>
                                                                        selectCompanionUser(idx, user)
                                                                    }
                                                                >
                                                                    <span>{user.name}</span>
                                                                    {user.display_name && (
                                                                        <span className="companion-dd-badge">
                                                                            {user.display_name}
                                                                        </span>
                                                                    )}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                            </div>
                                            <input
                                                type="tel"
                                                className="companion-input"
                                                value={c.hp}
                                                onChange={(e) =>
                                                    handleCompanionFieldChange(
                                                        idx,
                                                        "hp",
                                                        e.target.value.replace(/[^0-9]/g, "")
                                                    )
                                                }
                                                placeholder="연락처는 - 없이 숫자만 입력해주세요"
                                                inputMode="numeric"
                                                autoComplete="off"
                                            />
                                            <div className="companion-gender">
                                                <button
                                                    type="button"
                                                    className={`companion-gender__btn${
                                                        c.gender === "M" ? " active" : ""
                                                    }`}
                                                    onClick={() =>
                                                        handleCompanionFieldChange(idx, "gender", "M")
                                                    }
                                                >
                                                    남성
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`companion-gender__btn${
                                                        c.gender === "F" ? " active" : ""
                                                    }`}
                                                    onClick={() =>
                                                        handleCompanionFieldChange(idx, "gender", "F")
                                                    }
                                                >
                                                    여성
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </li>
                        )}
                        {path.includes("product") && (
                            <>
                                <li>
                                    <p className="price-label ">배송지</p>
                                    <p className="price-value bold">
                                        {!!address ? (
                                            <span>{`${address.zipcode} ${
                                                address.road_address
                                            } ${
                                                address.detail_address || ""
                                            }`}</span>
                                        ) : (
                                            <span
                                                onClick={() =>
                                                    navigate(
                                                        "/mypage/address/edit"
                                                    )
                                                }
                                                style={{
                                                    textDecoration: "underline",
                                                    color: "#4c32d4",
                                                    cursor: "pointer",
                                                }}
                                            >
                                                주소 입력하기
                                            </span>
                                        )}
                                    </p>
                                </li>

                                {!!address && (
                                    <p
                                        className="change-address"
                                        onClick={() => setIsOpen(true)}
                                    >
                                        배송지 변경하기
                                    </p>
                                )}

                                <li>
                                    <p className="price-label ">배송 메세지</p>
                                </li>
                                <textarea
                                    className="input"
                                    value={shippingMemo}
                                    onChange={(e) =>
                                        setShippingMemo(e.target.value)
                                    }
                                    rows={5}
                                    placeholder="배송 시 요청사항을 입력해주세요 (선택)"
                                    style={{
                                        border: "1px solid #e8eef2",
                                        padding: "16px",
                                    }}
                                />
                            </>
                        )}
                    </ul>
                </div>
            )}
            <div className="btn-wrap">
                <button
                    className={`btn btn-full btn-large ${isSubmitting || !isCompanionsValid ? 'btn-disabled' : 'btn-purple500'}`}
                    onClick={clickPaymentBtn}
                    disabled={isSubmitting || !isCompanionsValid}
                >
                    {isSubmitting ? "처리 중..." : "결제하기"}
                </button>
            </div>
            <CommonModal
                isOpen={isOpen}
                onRequestClose={() => {
                    setIsOpen(false);
                    setShowAddAddress(false);
                }}
                className={`change-address-modal-wrap`}
            >
                {showAddAddress ? (
                    <Postcode
                        address={{
                            is_primary:
                                (userInfo?.addresses?.length ?? 0) === 0,
                            id: null,
                        }}
                        saveAddress={handleSaveNewAddress}
                    />
                ) : (
                    <>
                        {userInfo?.addresses?.map((ad) => (
                            <div
                                className="address-card-wrap"
                                key={ad.id}
                                onClick={() => {
                                    setAddress(ad);
                                    setIsOpen(false);
                                }}
                                style={{
                                    border:
                                        address?.id === ad.id
                                            ? "1px solid #4c32d4"
                                            : "",
                                }}
                            >
                                <div className="address-card-contents">
                                    <div>
                                        <p
                                            className="bold"
                                            style={{ textAlign: "left" }}
                                        >
                                            <span>{ad.label}</span>
                                            <span
                                                className={
                                                    ad.is_primary
                                                        ? "badge no-margin badge-outline-purple"
                                                        : ""
                                                }
                                            >
                                                {ad.is_primary ? "대표" : ""}
                                            </span>
                                        </p>
                                        <div className="address-info">
                                            주소: {ad.zipcode} {ad.road_address}{" "}
                                            {ad.detail_address}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}

                        {(userInfo?.addresses?.length ?? 0) < 3 ? (
                            <div className="address-card-wrap empty-address">
                                <p
                                    className="empty-address-text"
                                    onClick={() => setShowAddAddress(true)}
                                >
                                    + 배송지 추가하기
                                </p>
                            </div>
                        ) : (
                            <p
                                className="address-limit-guide"
                                style={{
                                    fontSize: "11px",
                                    color: "#8b95a1",
                                    lineHeight: 1.5,
                                    marginTop: "8px",
                                }}
                            >
                                배송지는 3개까지 저장 가능합니다. 마이버클 &gt;
                                프로필 편집 &gt; 주소 편집 페이지에서 수정/삭제가
                                가능합니다.
                            </p>
                        )}
                    </>
                )}
            </CommonModal>
        </div>
    );
};

export default PaymentInfo;
